import { ReactNode } from "react";

import { getFaqAppLocations } from "../api/faq/getFaqAppLocations";
import { getHighlightedAppLocationViews } from "../api/faq/getHighlightedAppLocationViews";
import { getHighlightedFaqViews } from "../api/faq/getHighlightedFaqViews";
import { FaqContextIds } from "../components/AppLocation";
import AppLocation from "../components/AppLocation";
import { AppLocationRenderFunction } from "../helpers/faq";
import { APQueryOptions } from "../providers/APQueryProvider";
import { APPage, CRMAppLocationIdentifier, FaqProps } from "../types/Faq";

interface UseFaqsCheckProps {
  pageId: APPage;
  quoteId?: string;
  queryOptions?: APQueryOptions;
  contextIds?: FaqContextIds;
}

interface UseFaqsCheckReturn {
  faqProps: FaqProps;
  contextIds?: FaqContextIds;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useFaqs = ({
  pageId,
  quoteId,
  queryOptions,
  contextIds,
}: UseFaqsCheckProps): UseFaqsCheckReturn => {
  const faqAppLocations = getFaqAppLocations({ pageId, quoteId }, queryOptions);
  const highlightedFaqViews = getHighlightedFaqViews();
  const highlightedAppLocationViews = getHighlightedAppLocationViews();

  if (faqAppLocations.isError) {
    throw faqAppLocations.error;
  }
  if (highlightedFaqViews.isError) {
    throw highlightedFaqViews.error;
  }
  if (highlightedAppLocationViews.isError) {
    throw highlightedAppLocationViews.error;
  }

  const renderAppLocation: AppLocationRenderFunction = (
    appLocationIdentifier: CRMAppLocationIdentifier,
    children: ReactNode,
    hideIfNoFaqs?: boolean,
    hideHighlight?: boolean,
    triggerOnLoad?: boolean,
    persistentModal?: boolean
  ) => {
    return (
      <AppLocation
        appLocations={faqAppLocations.data || []}
        highlightedFaqViews={highlightedFaqViews.data || {}}
        highlightedAppLocationViews={highlightedAppLocationViews.data || {}}
        appLocationIdentifier={appLocationIdentifier}
        hideIfNoFaqs={hideIfNoFaqs}
        hideHighlight={hideHighlight}
        contextIds={contextIds}
        triggerOnLoad={triggerOnLoad}
        persistentModal={persistentModal}
      >
        {children}
      </AppLocation>
    );
  };

  const isLoading =
    faqAppLocations.isLoading ||
    highlightedFaqViews.isLoading ||
    highlightedAppLocationViews.isLoading;
  const isSuccess =
    faqAppLocations.isSuccess &&
    highlightedFaqViews.isSuccess &&
    highlightedAppLocationViews.isSuccess;

  return {
    faqProps: {
      faqAppLocations: faqAppLocations.data || [],
      highlightedFaqViews: highlightedFaqViews.data || {},
      highlightedAppLocationViews: highlightedAppLocationViews.data || {},
      renderAppLocation,
    },
    contextIds: contextIds,
    isLoading,
    isSuccess,
  };
};
